/**
 * SegmentStageFilter - Multi-select for opportunity board stages on a switch-step segment.
 *
 * Only renders when the parent template's trigger is `OpportunityCreatedAutomationTrigger`;
 * otherwise returns null. Empty selection means "any stage." Loads the board (and its stages)
 * from the trigger's `boardId` via `useInfiniteBoardsQuery` so the cache lines up with the
 * trigger picker's board list. See Levitate-API#11590.
 */

import * as Api from '@ViewModels';
import * as React from 'react';
import { Automations } from '../../../../../extViewmodels/Utils';
import { useInfiniteBoardsQuery } from '../../../../../queries';
import {
	Combobox,
	ComboboxChip,
	ComboboxChipRemove,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxItem,
	ComboboxItemCheckboxIndicator,
	ComboboxList,
	ComboboxValue,
	useComboboxAnchor,
} from '../../../../components-v2/Combobox';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';

interface ISegmentStageFilterProps {
	trigger?: Api.IAutomationTrigger;
	selectedStageIds?: string[];
	onSelectedStageIdsChanged?(stageIds: string[]): void;
	disabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}

interface IStageItem {
	id: string;
	label: string;
}

export const SegmentStageFilter: React.FC<ISegmentStageFilterProps> = props => {
	const { trigger, selectedStageIds = [], onSelectedStageIdsChanged, disabled, impersonationContext } = props;
	const anchor = useComboboxAnchor();

	const boardId = Automations.triggers.isOpportunityCreatedTrigger(trigger) ? trigger.boardId : undefined;

	const boardsQuery = useInfiniteBoardsQuery({
		type: 'Opportunity',
		impersonationContext,
		autoFetchAll: true,
		meta: {
			toastErrorMessage: 'Error loading opportunity stages',
		},
	});

	const stages: IStageItem[] = React.useMemo(() => {
		if (!boardId) return [];
		const board = boardsQuery.data?.pages.flatMap(page => page.values ?? []).find(b => b.id === boardId);
		return (board?.stages ?? [])
			.filter((s): s is Api.IBoardStageReference & { id: string } => !!s.id)
			.map(s => ({ id: s.id, label: s.name ?? '' }));
	}, [boardsQuery.data, boardId]);

	const stagesById = React.useMemo(() => {
		const map = new Map<string, IStageItem>();
		stages.forEach(s => map.set(s.id, s));
		return map;
	}, [stages]);

	const selectedItems = React.useMemo(
		() => selectedStageIds.map(id => stagesById.get(id) ?? { id, label: id }),
		[selectedStageIds, stagesById]
	);

	if (!boardId) {
		return null;
	}

	return (
		<Combobox
			items={stages}
			value={selectedItems}
			multiple
			disabled={disabled}
			itemToStringLabel={item => (item as IStageItem).label}
			isItemEqualToValue={(a, b) => (a as IStageItem).id === (b as IStageItem).id}
			onValueChange={value => {
				if (disabled) return;
				const next = (value as IStageItem[]).map(item => item.id);
				onSelectedStageIdsChanged?.(next);
			}}
		>
			<ComboboxChips ref={anchor}>
				<ComboboxValue>
					{(value: IStageItem[]) => (
						<>
							{value.map(item => (
								<ComboboxChip key={item.id} aria-label={item.label}>
									{item.label}
									{!disabled && <ComboboxChipRemove aria-label={`Remove ${item.label}`} />}
								</ComboboxChip>
							))}
							<ComboboxChipsInput placeholder={value.length ? '' : 'Any stage'} />
						</>
					)}
				</ComboboxValue>
			</ComboboxChips>
			<ComboboxContent anchor={anchor}>
				<ComboboxEmpty>
					{boardsQuery.isLoading && !stages.length ? <LoadingSpinner type='tiny' /> : 'No stages found'}
				</ComboboxEmpty>
				<ComboboxList>
					{(item: IStageItem) => (
						<ComboboxItem key={item.id} value={item}>
							<ComboboxItemCheckboxIndicator />
							{item.label}
						</ComboboxItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};
